import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { Button } from "@tento-ui/components/button/button";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { Result } from "@tento-core/result";
import { ResponseError } from "@tento-core/typed-api";
import { Tracing } from "@tento-core/tracing";
import { Network } from "../@generated/network";

@component({ name: "lona-create-api-key-page" })
export class CreateApiKeyPage extends LonaWebComponent {
  private $setup: HTMLElement = this.$("setup");
  private $success: HTMLElement = this.$("success");
  private $create: HTMLButtonElement = this.$("create");
  private $cancel: HTMLButtonElement = this.$("cancel");
  private $token: HTMLInputElement = this.$("token");
  private $copy: HTMLButtonElement = this.$("copy");
  private $status: HTMLElement = this.$("status");
  private props: Option<CreateApiKeyPage.Props>;
  private binding = 0;

  constructor() {
    super();
    this.bindButton(this.$create, () => { void this.createKey(); });
    this.bindButton(this.$cancel, () => this.props?.onDone?.());
    this.bindButton(this.$("done"), () => this.props?.onDone?.());
    this.bindButton(this.$copy, () => { void this.copyKey(); });
  }

  bind(props: CreateApiKeyPage.Props): this {
    this.reset();
    this.props = props;
    return this;
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.reset();
  }

  onRecycle(): void {
    this.reset();
    super.onRecycle();
  }

  private reset(): void {
    this.binding++;
    this.props = null;
    this.$token.value = "";
    this.$setup.hidden = false;
    this.$success.hidden = true;
    this.$create.disabled = false;
    this.$cancel.disabled = false;
    this.$create.textContent = "Create key";
    this.$copy.textContent = "Copy key";
    this.$status.textContent = "";
    this.$("expiry").textContent = "";
  }

  private bindButton($button: HTMLButtonElement, onClick: EmptyFunction): void {
    GESTURE_MANAGER.addPointerEvent($button, { onClick });
    $button.onkeydown = event => {
      if ($button.disabled || (event.key !== "Enter" && event.key !== " ")) return;
      event.preventDefault();
      event.stopPropagation();
      onClick();
    };
  }

  private async copyKey(): Promise<void> {
    const binding = this.binding;
    try {
      await navigator.clipboard.writeText(this.$token.value);
      if (binding !== this.binding) return;
      this.$copy.textContent = "Copied";
      this.$status.textContent = "Key copied to clipboard.";
    } catch {
      if (binding !== this.binding) return;
      this.$token.select();
      this.$status.textContent = "Select and copy the key above.";
    }
  }

  private async createKey(): Promise<void> {
    const props = this.props;
    if (!props || this.$create.disabled) return;
    const binding = this.binding;
    this.$create.disabled = true;
    this.$cancel.disabled = true;
    this.$create.textContent = "Creating…";
    this.$status.textContent = "";
    try {
      const result = await props.onCreate();
      if (binding !== this.binding) return;
      result.match({
        ok: key => {
          this.$token.value = key.token;
          this.$setup.hidden = true;
          this.$success.hidden = false;
          this.$("expiry").textContent = `Expires ${key.expiresAt.ndt.date.format()}`;
        },
        err: error => {
          this.$status.textContent = ResponseError.sentence(error, "Could not create a key. Please try again.");
        },
      });
    } catch {
      if (binding !== this.binding) return;
      Tracing.err("net")("API key creation request failed");
      this.$status.textContent = "Could not create a key. Please try again.";
    } finally {
      if (binding === this.binding) {
        this.$create.disabled = false;
        this.$cancel.disabled = false;
        this.$create.textContent = "Create key";
      }
    }
  }

  static $styles = [
    Button.$style,
    css`
      :host { display: block; width: 100%; min-height: 360px; box-sizing: border-box; }
      #root { padding: 28px 32px 32px; gap: 20px; }
      #setup, #success { gap: 24px; }
      p { --font-size: var(--text-10px); --line-height-ratio: 1.5; color: var(--text-sec); }
      h2 { --font-size: var(--text-13px); font-weight: 500; }
      #details { border: 1px solid var(--divider); border-radius: 10px; }
      .detail { padding: 16px 18px; display: flex; align-items: center; justify-content: space-between; gap: 20px; }
      .detail + .detail { border-top: 1px solid var(--divider); }
      .label, .value { --font-size: var(--text-10px); --line-height-ratio: 1.2; }
      .label { color: var(--text-sec); }
      .value { color: var(--text-pri); }
      #actions, #success-actions { display: flex; justify-content: flex-end; gap: 10px; }
      button { --button-padding: 10px 18px; --button-radius: 8px; }
      #hint, #expiry { --font-size: var(--text-9px); color: var(--text-ter); }
      #key-field { display: flex; align-items: center; padding: 8px; gap: 8px; border: 1px solid var(--divider); border-radius: 10px; background: var(--bg-surface-light); }
      #token { min-width: 0; width: 100%; flex: 1; padding: 8px; border: 0; background: transparent; color: var(--text-pri); font: inherit; font-family: monospace; outline-offset: 2px; }
      #copy { flex: none; --button-padding: 8px 12px; }
      #token-label { color: var(--text-pri); --font-size: var(--text-10px); }
      #status { --font-size: var(--text-9px); }
      #status:empty { display: none; }
      [hidden] { display: none !important; }
      @media (max-width: 600px) { #root { padding: 24px 20px; } .detail { padding: 16px; } }
    `,
  ];

  static $html = html`
    <std-col id="root">
      <std-col id="setup">
        <p>Create a key for a tool you trust. It will have the same access to Lona as your account.</p>
        <std-col id="details">
          <std-row class="detail"><span class="label">Access</span><span class="value text">Your account</span></std-row>
          <std-row class="detail"><span class="label">Permissions</span><span class="value text">Read and write</span></std-row>
          <std-row class="detail"><span class="label">Expires</span><span class="value text">In 30 days</span></std-row>
        </std-col>
        <p id="hint">You’ll only see the full key once. Have your tool’s credential settings ready.</p>
        <std-row id="actions">
          <button type="button" id="cancel" secondary>Cancel</button>
          <button type="button" id="create" primary>Create key</button>
        </std-row>
      </std-col>
      <std-col id="success" hidden>
        <h2>Your key is ready</h2>
        <p>Copy it now and save it in your tool’s credential settings. You won’t be able to view the full key again.</p>
        <std-col style="gap:12px;">
          <label id="token-label" for="token">API key</label>
          <std-row id="key-field">
            <input id="token" type="text" readonly autocomplete="off" spellcheck="false" />
            <button type="button" id="copy" secondary>Copy key</button>
          </std-row>
          <p id="expiry"></p>
        </std-col>
        <std-row id="success-actions"><button type="button" id="done" primary>Done</button></std-row>
      </std-col>
      <p id="status" role="status" aria-live="polite"></p>
    </std-col>
  `;
}

export namespace CreateApiKeyPage {
  export type Props = {
    onCreate: () => Promise<Result<Network.CreatedApiKeyResponse, ResponseError>>;
    onDone: Option<EmptyFunction>;
  };
}
